import React, { useState, useEffect } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { getImageUrl } from '../utils/api';
import { useSEO } from '../utils/seo';
import { ICON_MAP, getIcon } from './ArtistLinksPage';
import { toast } from 'sonner';
import './AMRLinksPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export default function AMRLinksPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useSEO({
    title: 'Anyone Music Records — Links',
    description: 'Find Anyone Music Records on all platforms. Latest releases, events, and more.',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/amr-links`);
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Subscribed!');
        setEmail('');
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Already subscribed');
      }
    } catch { toast.error('Failed to subscribe'); }
    finally { setSubscribing(false); }
  };

  if (loading) return (
    <div className="amr-lp"><div className="amr-lp__loader"><Loader2 size={28} className="amr-lp__spin" /></div></div>
  );

  const { featured = [], music_links = [], social_links = [], custom_links = [], releases = [], promos = [], events = [] } = data || {};
  const hasContent = featured.length || music_links.length || social_links.length || custom_links.length || releases.length || promos.length || events.length;

  return (
    <div className="amr-lp" data-testid="amr-links-page">
      <div className="amr-lp__container">
        {/* Header */}
        <header className="amr-lp__header" data-testid="amr-links-header">
          <img src="/amr-logo.png" alt="AMR" className="amr-lp__logo" />
          <h1 className="amr-lp__name">ANYONE MUSIC RECORDS</h1>
          <p className="amr-lp__tagline">Electronic Music Label</p>
        </header>

        {!hasContent ? (
          <div className="amr-lp__empty"><p>Coming soon</p></div>
        ) : (
          <div className="amr-lp__sections">
            {/* Featured Links */}
            {featured.length > 0 && (
              <section className="amr-lp__section" data-testid="featured-section">
                {featured.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
                    className="amr-lp__card amr-lp__card--featured" data-testid={`featured-link-${link.id}`}>
                    {link.icon_image ? (
                      <img src={getImageUrl(link.icon_image)} alt="" className="amr-lp__card-thumb" />
                    ) : (
                      <span className="amr-lp__card-icon">{getIcon(link.icon)}</span>
                    )}
                    <span className="amr-lp__card-title">{link.title}</span>
                    <span className="amr-lp__card-arrow">&rsaquo;</span>
                  </a>
                ))}
              </section>
            )}

            {/* Pre-Sale / New Releases */}
            {promos.length > 0 && (
              <section className="amr-lp__section" data-testid="promos-section">
                <h2 className="amr-lp__section-label">
                  {promos.some(p => p.is_pre_release) ? 'Pre-Sale / New Releases' : 'New Releases'}
                </h2>
                {promos.map(p => {
                  const href = p.beatport_url || `/releases`;
                  const isExternal = !!p.beatport_url;
                  return (
                    <a key={p.id} href={href} target={isExternal ? '_blank' : '_self'} rel="noreferrer"
                      className="amr-lp__card amr-lp__card--promo" data-testid={`promo-link-${p.id}`}>
                      {p.cover_art && (
                        <img src={getImageUrl(p.cover_art)} alt="" className="amr-lp__card-thumb" />
                      )}
                      <div className="amr-lp__card-info">
                        <span className="amr-lp__card-title">{p.title}</span>
                        <span className="amr-lp__card-sub">
                          {p.artist_name}
                          {p.is_pre_release && <span className="amr-lp__tag-inline">PRE-SALE</span>}
                          {p.beatport_url && <span className="amr-lp__tag-inline amr-lp__tag-inline--bp">BEATPORT</span>}
                        </span>
                      </div>
                      <span className="amr-lp__card-arrow">&rsaquo;</span>
                    </a>
                  );
                })}
              </section>
            )}

            {/* Latest Releases */}
            {releases.length > 0 && (
              <section className="amr-lp__section" data-testid="releases-section">
                <h2 className="amr-lp__section-label">Latest Releases</h2>
                {releases.map(r => (
                  <a key={r.id} href={`/releases/${r.slug || r.id}`}
                    className="amr-lp__card" data-testid={`release-link-${r.id}`}>
                    {r.artwork && (
                      <img src={getImageUrl(r.artwork)} alt="" className="amr-lp__card-thumb" />
                    )}
                    <div className="amr-lp__card-info">
                      <span className="amr-lp__card-title">{r.title}</span>
                      <span className="amr-lp__card-sub">{r.artist_name}</span>
                    </div>
                    <span className="amr-lp__card-arrow">&rsaquo;</span>
                  </a>
                ))}
              </section>
            )}

            {/* Music Platform Links */}
            {music_links.length > 0 && (
              <section className="amr-lp__section" data-testid="music-links-section">
                <h2 className="amr-lp__section-label">Listen</h2>
                {music_links.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
                    className="amr-lp__card" data-testid={`music-link-${link.id}`}>
                    {link.icon_image ? (
                      <img src={getImageUrl(link.icon_image)} alt="" className="amr-lp__card-thumb" />
                    ) : (
                      <span className="amr-lp__card-icon">{getIcon(link.icon)}</span>
                    )}
                    <span className="amr-lp__card-title">{link.title}</span>
                    <span className="amr-lp__card-arrow">&rsaquo;</span>
                  </a>
                ))}
              </section>
            )}

            {/* Events */}
            {events.length > 0 && (
              <section className="amr-lp__section" data-testid="events-section">
                <h2 className="amr-lp__section-label">Upcoming Events</h2>
                {events.map(ev => (
                  <a key={ev.id} href={`/events/${ev.slug || ev.id}`}
                    className="amr-lp__card amr-lp__card--event" data-testid={`event-link-${ev.id}`}>
                    <span className="amr-lp__card-icon">{getIcon('calendar')}</span>
                    <div className="amr-lp__card-info">
                      <span className="amr-lp__card-title">{ev.title}</span>
                      <span className="amr-lp__card-sub">{ev.venue}{ev.city ? `, ${ev.city}` : ''}</span>
                    </div>
                    {ev.date && <span className="amr-lp__event-date">{formatDate(ev.date)}</span>}
                  </a>
                ))}
              </section>
            )}

            {/* Custom Links */}
            {custom_links.length > 0 && (
              <section className="amr-lp__section" data-testid="custom-links-section">
                {custom_links.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
                    className="amr-lp__card" data-testid={`custom-link-${link.id}`}>
                    {link.icon_image ? (
                      <img src={getImageUrl(link.icon_image)} alt="" className="amr-lp__card-thumb" />
                    ) : (
                      <span className="amr-lp__card-icon">{getIcon(link.icon)}</span>
                    )}
                    <span className="amr-lp__card-title">{link.title}</span>
                    <span className="amr-lp__card-arrow">&rsaquo;</span>
                  </a>
                ))}
              </section>
            )}

            {/* Social Icons Row */}
            {social_links.length > 0 && (
              <section className="amr-lp__socials" data-testid="social-section">
                {social_links.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
                    className="amr-lp__social-icon" title={link.title} data-testid={`social-link-${link.id}`}>
                    {getIcon(link.icon)}
                  </a>
                ))}
              </section>
            )}

            {/* Newsletter */}
            <section className="amr-lp__newsletter" data-testid="newsletter-section">
              <p className="amr-lp__newsletter-label">Stay in the loop</p>
              <form className="amr-lp__newsletter-form" onSubmit={handleSubscribe}>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" required data-testid="newsletter-email-input" />
                <button type="submit" disabled={subscribing} data-testid="newsletter-submit-btn">
                  {subscribing ? <Loader2 size={16} className="amr-lp__spin" /> : <Mail size={16} />}
                </button>
              </form>
            </section>
          </div>
        )}

        {/* Footer */}
        <footer className="amr-lp__footer">
          <a href="https://anyonemusicrecords.com" className="amr-lp__footer-link" data-testid="amr-homepage-link">
            anyonemusicrecords.com
          </a>
          <p>&copy; {new Date().getFullYear()} Anyone Music Records</p>
        </footer>
      </div>
    </div>
  );
}
